import { IStreamSubscriber, ISubscription } from "@microsoft/signalr";
import { HubInfo } from "./real-time-connection.constants";

export interface RealTimeStreamSubscriber {
  hubInfo: HubInfo;
  args?: unknown[];
  beforeSubscribeAction?: unknown;
  streamSubscriber: StreamSubscriber<unknown>;
  connection?: ISubscription<unknown>;
}

export class StreamSubscriber<T> implements IStreamSubscriber<T> {

  /**
   * `true` once the stream reached a terminal state - it completed, it errored, the transport died,
   * or we disposed its subscription - so there is nothing left to tear down. `undefined` means it
   * was never subscribed.
   *
   * @microsoft/signalr never writes this flag: it is ours, and the only writers are
   * `RealTimeConnection.stream` (which sets it from the wrapped complete/error callbacks and resets
   * it on every fresh subscribe) and the two helpers below. Tear a stream down through those
   * helpers rather than guarding on `closed === false` when what you mean is "not finished yet":
   * that spelling reads "never subscribed" as "already closed", and is what stopped
   * `disposeSteadyStream` from ever disposing anything.
   */
  closed?: boolean;

  constructor(
    public callingAction: { type: string },
    public next: (value: T) => void,
    public complete: () => void = () => console.debug(`Completed: ${this.callingAction?.type}`),
    public error: (err) => void = err => console.error(`Stream: ${this.callingAction?.type}, ${err}`)
  ) { }
}

/**
 * Records that a subscriber's stream is over without touching the wire. Use this when something
 * else already ended it - the hub connection closed, or the stream never got subscribed - so that
 * no CancelInvocation is sent over a transport that cannot carry it.
 */
export function markStreamSubscriptionClosed(subscriber?: RealTimeStreamSubscriber): void {
  if (!subscriber?.streamSubscriber) {
    return;
  }
  subscriber.streamSubscriber.closed = true;
  subscriber.connection = undefined;
}

/**
 * Cancels a subscriber's SignalR subscription unless it is already closed, and marks it closed so a
 * second call is a no-op. The flag has to be real because signalr's `SubjectSubscription.dispose()`
 * is not idempotent: once the observer list is empty it fires the subject's cancel callback again,
 * sending another CancelInvocation for an invocation the server has already finished.
 */
export function disposeStreamSubscription(subscriber?: RealTimeStreamSubscriber): void {
  if (!subscriber?.streamSubscriber || subscriber.streamSubscriber.closed === true) {
    return;
  }
  const subscription = subscriber.connection;
  // Mark before disposing: disposing can re-enter here through the subscriber's error callback.
  markStreamSubscriptionClosed(subscriber);
  subscription?.dispose();
}
